import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Award, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { familyReportCardQuery, familyResultsQuery } from '@/features/exams/api'
import { GradeBadge } from '@/features/exams/components/ExamBadges'
import { ReportCardView } from '@/features/exams/components/ReportCardView'
import { familyQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatPercent } from '@/utils/format'
import { formatOutOf } from '@/features/exams/status'

/** Family portal: published exam results of the student(s) linked to this login. */
export function FamilyResultsPage() {
  const { t } = useTranslation('exams')
  useDocumentTitle('Results')
  const family = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)
  const [params, setParams] = useSearchParams()
  const examId = Number(params.get('exam') ?? 0)

  if (family.isPending) return <LoadingState />
  if (family.error) return <ErrorState onRetry={() => void family.refetch()} />
  const child = family.data[selected] ?? family.data[0]

  return (
    <>
      <PageHeader
        title={t('Results')}
        description={t('Published exam results and report cards.')}
        breadcrumbs={[{ label: t('Results') }]}
        homeHref="/portal"
      />
      {family.data.length > 1 && !examId ? (
        <div role="tablist" aria-label={t('Choose child')} className="mb-6 flex gap-2 overflow-x-auto">
          {family.data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {!child ? (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No student linked to your account')}
            description={t("Ask the school office to link your login to your child's record.")}
          />
        </Card>
      ) : examId ? (
        <FamilyReportCard studentId={child.student.id} examId={examId} onBack={() => setParams({})} />
      ) : (
        <ResultsList
          studentId={child.student.id}
          name={child.student.firstName}
          onOpen={(id) => setParams({ exam: String(id) })}
        />
      )}
    </>
  )
}

function ResultsList({
  studentId,
  name,
  onOpen,
}: {
  studentId: number
  name: string
  onOpen: (examId: number) => void
}) {
  const { t } = useTranslation('exams')
  const results = useQuery(familyResultsQuery(studentId))
  if (results.isPending) return <LoadingState />
  if (results.error)
    return (
      <Card>
        <ErrorState onRetry={() => void results.refetch()} />
      </Card>
    )
  if (results.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Award />}
          title={t('No results yet')}
          description={t('Results for {{name}} appear here once the school publishes them.', { name })}
        />
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader
        icon={<Award />}
        title={t("{{name}}'s results", { name })}
        description={t('{{count}} published exams', { count: results.data.length })}
      />
      <ul className="divide-y divide-line">
        {results.data.map((r) => (
          <li key={r.examId}>
            <button
              type="button"
              onClick={() => onOpen(r.examId)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-muted"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{r.examName}</p>
                <p className="text-xs text-muted">
                  {r.examType} · {formatDate(r.startsOn)} – {formatDate(r.endsOn)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-ink tabular">
                  {r.outOf ? formatOutOf(r.average, r.outOf, 2) : formatPercent(r.percentage)}
                </p>
                <p className="text-xs text-muted">{t('Rank {{rank}} in section', { rank: r.sectionRank })}</p>
              </div>
              <GradeBadge grade={r.grade} isPass={r.isPass} size="md" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function FamilyReportCard({
  studentId,
  examId,
  onBack,
}: {
  studentId: number
  examId: number
  onBack: () => void
}) {
  const { t } = useTranslation('exams')
  const card = useQuery(familyReportCardQuery(studentId, examId))
  return (
    <div className="space-y-4">
      <div className="print:hidden">
        <Button variant="secondary" leftIcon={<ArrowLeft />} onClick={onBack}>
          {t('All results')}
        </Button>
      </div>
      {card.isPending ? (
        <LoadingState />
      ) : card.error ? (
        <Card>
          <ErrorState onRetry={() => void card.refetch()} />
        </Card>
      ) : (
        <ReportCardView card={card.data} />
      )}
    </div>
  )
}
